/**
 * pricingStrategyUtils.js
 *
 * Utils compartidos del módulo pricing strategy v2:
 *  - Iconos del grafo de reglas (trigger/acción)
 *  - Frases humanas de una regla (condición, acción, frecuencia)
 *  - Helpers de color (alpha, colores de tags de plantillas)
 *
 * Las funciones de frases reciben `vm` (la instancia Vue del componente)
 * para acceder a $t y al hotel actual del store.
 */

import numeral from "numeral";

// ── Iconos del grafo (trigger → condiciones → acción) ───────────────────────
// Los iconos de condición no van aquí: son data-driven desde
// state.rule_types[].icon (getter getPricingStrategyRuleTypeByCode).

export const FREQUENCY_ICONS = {
  hourly: "mdi-clock-fast",
  daily: "mdi-calendar-today",
  weekly: "mdi-calendar-week",
  monthly: "mdi-calendar-month",
  // Los guardarailes no tienen horario propio: se evalúan tras cada cálculo
  on_change: "mdi-calculator-variant-outline"
};

export const ACTION_ICONS = {
  manual: "mdi-gesture-tap",
  market: "mdi-storefront-outline",
  // Acciones de guardarail
  set_value: "mdi-currency-eur",
  notify: "mdi-bell-outline",
  // Retirado, pero se conserva por si queda algún dato antiguo
  clamp: "mdi-arrow-collapse-vertical"
};

export function frequencyIcon(frequency) {
  return FREQUENCY_ICONS[frequency] || "mdi-clock-outline";
}

export function actionIcon(mode) {
  return ACTION_ICONS[mode] || "mdi-rocket-launch-outline";
}

// ── Acción con valor por ruta ───────────────────────────────────────────────
//
// El MODO de la acción es siempre de la regla; el VALOR puede ser único o de
// cada ruta, y lo elige el usuario con un botón. Un guardarail de suelo y techo
// tiene dos rutas ("precio menor que X" y "precio mayor que Y") y cada una fija
// un importe distinto; una regla de temporada alta puede subir un 10% por una
// rama y un 5% por la otra.
//
// Vale para todos los modos. El contrato con el backend es la PRESENCIA del
// bloque de valor en la ruta: si routes[i].action existe manda ese valor, y si
// no, se usa la acción global. Por eso no hace falta ninguna bandera en el
// payload.

// Los modos conocidos, usados para distinguir el shape interno del canvas (un
// bucket por modo) del shape plano del backend.
const ACTION_MODES = ["manual", "market", "set_value", "notify"];

// ¿Esta regla lleva valores por ruta? Es una pregunta sobre la FORMA del dato,
// no sobre el modo: cualquier modo puede llevarlos.
//
// Sin tope de rutas a propósito: una regla de una sola ruta que traiga su valor
// dentro de la ruta tiene que enseñar ESE valor, no el global, que puede venir
// vacío. El "solo con 2+ rutas" es cosa del botón, y vive en el canvas.
export function hasPerRouteAction(rule) {
  return !!(rule && (rule.routes || []).some(route => route.action));
}

// Aplana el valor de una ruta sobre la acción global, para poder reutilizar
// todo lo que ya sabe interpretar una acción. Acepta tanto el shape interno
// del canvas (un bucket por modo) como el del backend (plano).
export function effectiveActionFor(action, route) {
  const mode = action && action.mode;
  const routeAction = route && route.action;
  if (!mode || !routeAction) return action;
  // Un draft viejo puede no tener el bucket del modo activo. Sin esta
  // distinción el fallback esparciría el objeto de buckets entero sobre la
  // acción ({set_value: {...}, market: {...}} como si fueran campos suyos).
  const isBucketed = ACTION_MODES.some(
    m => routeAction[m] && typeof routeAction[m] === "object"
  );
  const bucket = isBucketed ? routeAction[mode] : routeAction;
  if (!bucket) return action;
  return { ...action, ...bucket };
}

// ── Color ────────────────────────────────────────────────────────────────────

// "#RGB"/"#RRGGBB" → "rgba(r, g, b, alpha)"
export function alphaColor(hex, alpha) {
  let h = (hex || "#666666").replace("#", "");
  if (h.length === 3) {
    h = h
      .split("")
      .map(c => c + c)
      .join("");
  }
  const n = parseInt(h, 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
}

export const DEFAULT_TAG_COLOR = "#666666";

// Color de un tag de plantilla (viene del backend) + tinte de fondo derivado
export function tagColorSet(color) {
  const base = color || DEFAULT_TAG_COLOR;
  return { color: base, tint: alphaColor(base, 0.12) };
}

// Color de fondo neutro del lienzo. No es un color de regla válido: se usa como
// fondo y no aparece como opción real seleccionable, así que hay que excluirlo.
export const RULE_CANVAS_BG_COLOR = "#f0f2f5";

// ¿El color es vacío o el gris de fondo neutro (no seleccionable como regla)?
export function isNeutralRuleColor(color) {
  return !color || color.toLowerCase() === RULE_CANVAS_BG_COLOR;
}

// Elige un color aleatorio de la paleta de la cadena (los que el usuario puede
// escoger en pantalla), excluyendo el gris de fondo neutro. Si la paleta está
// vacía, devuelve el fallback indicado.
export function randomRuleColor(colors, fallback = "#2e21bd") {
  const palette = (colors || []).filter(c => !isNeutralRuleColor(c));
  if (!palette.length) return fallback;
  return palette[Math.floor(Math.random() * palette.length)];
}

// ── Localización de números en frases ───────────────────────────────────────
// Los valores que se interpolan en las traducciones (ocupación, roomnights,
// importe…) llegan crudos del backend con "." como separador decimal. numeral
// ya está configurado con el numeral_locale del usuario (login), así que usamos
// su separador decimal para mostrar "." o "," según idioma, igual que el resto
// de la app.

// Separador decimal del idioma activo (fallback "." si numeral no lo expone).
export function decimalSeparator() {
  try {
    const delimiters = numeral.localeData().delimiters;
    return (delimiters && delimiters.decimal) || ".";
  } catch (e) {
    return ".";
  }
}

// Localiza un único valor: solo reescribe el separador decimal cuando el valor
// tiene parte decimal. Enteros y valores no numéricos se devuelven intactos.
export function localizeNumber(value) {
  const sep = decimalSeparator();
  if (sep === ".") return value;
  if (typeof value === "number" && Number.isFinite(value)) {
    return String(value).replace(".", sep);
  }
  if (typeof value === "string" && /^-?\d+\.\d+$/.test(value)) {
    return value.replace(".", sep);
  }
  return value;
}

// Copia superficial de un objeto de interpolación i18n con sus valores
// numéricos localizados (separador decimal según idioma).
export function localizeNumbers(params) {
  const out = {};
  Object.keys(params || {}).forEach(key => {
    out[key] = localizeNumber(params[key]);
  });
  return out;
}

// ── Frases humanas de una regla v2 ───────────────────────────────────────────
// Único punto donde se construyen. Lo consumen el grafo (tooltips), las
// cards del grid, el mensaje de ayuda del grid y el dialog de plantillas.

// Resume corto de una condición: "Cada {n} roomnights", "Ocupación entre…"
export function conditionResume(vm, condition) {
  return vm.$t(
    `pricing_strategy_rule_type_resume_${condition.rule_type_code}`,
    localizeNumbers(condition.settings || {})
  );
}

// Literal humano de un spot de pickup ("Pickup 7 días", …) según los
// pickup_spots configurados en el hotel actual
export function pickupSpotLiteral(vm, spot) {
  if (!spot) return "";
  if (spot.startsWith("pickup-")) {
    const number = spot.split("-")[1];
    // Para pickup-0, usar traducción directa
    if (number === "0") {
      return vm.$t(`pickup_${number}`);
    }
    // Para pickup-1 a pickup-6, usar pickup_spots del hotel
    const hotel = vm.$store.getters.current_hotel;
    const literal =
      hotel &&
      hotel.settings &&
      hotel.settings.pickup_spots &&
      hotel.settings.pickup_spots[`pickup_${number}`];
    if (literal) {
      return vm.$t("pickup") + " " + literal;
    }
  }
  return spot.replace("-", " ");
}

// Frase completa de una condición: resume + spot si aplica
export function conditionPhrase(vm, condition) {
  const settings = condition.settings || {};
  let text = conditionResume(vm, condition);
  if (settings.condition_spot) {
    text += " · " + pickupSpotLiteral(vm, settings.condition_spot);
  }
  return text;
}

// Frase de una ruta: sus condiciones (AND) unidas
export function routePhrase(vm, route) {
  return (route.conditions || [])
    .map(condition => conditionPhrase(vm, condition))
    .join(" + ");
}

// "15€" / "-5%" a partir del action manual (mismo formato que resume.action)
export function actionAmountLabel(action) {
  return `${localizeNumber(action.amount)}${
    action.unit === "fixed" ? "€" : "%"
  }`;
}

// Frase de la acción.
//
// Se compone SIEMPRE en cliente. Antes se prefería el `resume` precalculado del
// backend, pero eso son cadenas crudas de su modelo interno ("notify P2",
// "high", "market") que se pintaban tal cual en tooltips y listados. Aquí están
// todos los datos y las traducciones, así que no hace falta.
export function actionPhrase(vm, action) {
  if (!action) return "";
  // Guardarail: fija un precio absoluto
  if (action.mode === "set_value") {
    return vm.$t("pricing_strategy_graph_action_set_value", {
      amount: `${localizeNumber(action.value)}€`
    });
  }
  // Guardarail: avisa sin tocar el precio
  if (action.mode === "notify") {
    return vm.$t("pricing_strategy_graph_action_notify", {
      priority: vm.$t(`guardrail_priority_${action.priority || 2}`)
    });
  }
  if (action.mode === "market") {
    return vm.$t("pricing_strategy_graph_action_market", {
      range: vm.$t(`action_compset_pos_${action.compset_range}`)
    });
  }
  return vm.$t("pricing_strategy_graph_action_manual", {
    amount: actionAmountLabel(action)
  });
}

// Frase de la acción de UNA ruta: la global con el valor de esa ruta encima.
export function routeActionPhrase(vm, rule, route) {
  return actionPhrase(vm, effectiveActionFor(rule && rule.action, route));
}

// Frase completa de una regla: sus condiciones y su acción. Es lo que se
// enseña en listados y diálogos, donde no hay sitio para el grafo.
//
// Con valor por ruta cada rama lleva la suya, así que se emite
// "condiciones → acción" por ruta; con valor único, la acción va una sola vez
// al final.
export function ruleSummaryPhrase(vm, rule) {
  const routes = (rule && rule.routes) || [];
  if (!routes.length) return "";
  const perRoute = hasPerRouteAction(rule);
  const parts = routes.map(route => {
    const conditions = routePhrase(vm, route);
    if (!perRoute) return conditions;
    return `${conditions} → ${routeActionPhrase(vm, rule, route)}`;
  });
  const text = parts.join(` ${vm.$t("or")} `);
  return perRoute ? text : `${text} → ${actionPhrase(vm, rule.action)}`;
}

// Frase de la frecuencia de evaluación
export function frequencyPhrase(vm, schedule) {
  const frequency = (schedule && schedule.frequency) || "";
  return frequency ? vm.$t(`evaluation_frequency_${frequency}`) : "";
}

// ── Sugerencias (get-pricing-suggestions) ────────────────────────────────────

// Unidad del valor medido de cada tipo de condición. El compset no lleva:
// su medida es un nombre de posición, no una cifra.
const MEASURE_UNIT_BY_RULE_TYPE = {
  "pickup-roomnights": " rn",
  "pickup-roomnights-negative": " rn",
  "occupancy-range": "%",
  "occupancy-compset": "%",
  "price-lower-than": "€",
  "price-greater-than": "€",
  "compset-range": ""
};

// Valor medido de una condición ya evaluada, con su unidad: "67€", "89%",
// "6 rn", "Máx". Devuelve "" si la condición no llegó a evaluarse.
//
// No se repite el umbral: ya está en la frase de la condición, justo al lado.
export function conditionMeasureValue(vm, condition) {
  if (
    !condition ||
    condition.actual === null ||
    condition.actual === undefined
  ) {
    return "";
  }

  if (condition.ruleTypeCode === "compset-range") {
    return vm.$t(`action_compset_pos_${condition.actual}`);
  }

  const unit = MEASURE_UNIT_BY_RULE_TYPE[condition.ruleTypeCode] || "";
  return `${localizeNumber(condition.actual)}${unit}`;
}

// Índice de la ruta que hizo saltar la regla, o null si la sugerencia es
// anterior al campo `evaluation` y no lo sabemos.
export function triggeredRouteIndex(suggestion) {
  const index =
    suggestion && suggestion.evaluation
      ? suggestion.evaluation.routeIndex
      : null;
  return Number.isInteger(index) ? index : null;
}

// Settings de la condición que originó una sugerencia. El endpoint devuelve
// en rule_settings el snapshot completo de la regla ({action, routes,
// schedule}).
//
// Con `evaluation` sabemos exactamente qué ruta disparó y cogemos su primera
// condición. Sin él (sugerencias anteriores al campo) hay que adivinar por
// tipo, y eso se equivoca en cuanto dos rutas comparten tipo o cuando el
// rule_type_code de la regla no es el de la ruta que saltó.
export function suggestionConditionSettings(suggestion) {
  const rs = (suggestion && suggestion.rule_settings) || {};
  if (!rs.routes) {
    return rs;
  }

  const routeIndex = triggeredRouteIndex(suggestion);
  if (routeIndex !== null) {
    const route = rs.routes[routeIndex];
    const conditions = (route && route.conditions) || [];
    const byType = conditions.find(
      c => c.rule_type_code === suggestion.rule_type_code
    );
    return (byType || conditions[0] || {}).settings || {};
  }

  const conditions = [];
  rs.routes.forEach(route =>
    (route.conditions || []).forEach(c => conditions.push(c))
  );
  const match =
    conditions.find(c => c.rule_type_code === suggestion.rule_type_code) ||
    conditions[0];
  return (match && match.settings) || {};
}

// Frase de la medida tomada para una sugerencia: interpola los settings de
// la condición + measure ({kpi, amount}) en la clave measure del tipo
export function measurePhrase(vm, suggestion) {
  if (!suggestion || !suggestion.rule_type_code) return "";
  return vm.$t(
    `pricing_strategy_rule_type_measure_${suggestion.rule_type_code}`,
    localizeNumbers(
      Object.assign(
        {},
        suggestionConditionSettings(suggestion),
        suggestion.measure
      )
    )
  );
}

// Literal del spot de pickup de la condición de una sugerencia ("" si no aplica)
export function suggestionSpotLiteral(vm, suggestion) {
  const settings = suggestionConditionSettings(suggestion);
  return settings.condition_spot
    ? pickupSpotLiteral(vm, settings.condition_spot)
    : "";
}
